import React, { useEffect, useRef, useState } from 'react';
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  deleteDoc,
  where,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { DateTime } from 'luxon';
import { db } from '../firebase';
import Avatar from './Avatar';
import VoiceNote from './VoiceNote';
import ReportModal from './ReportModal';
import { useLanguage } from '../i18n';

const PAYSTACK_PUBLIC_KEY = 'pk_live_ced368f17237c0a072b6db63d641ca539b9ca6be';
const MONTHLY_PLAN_CODE = 'PLN_fdt81n4wnr0z85q';
const SIX_MONTH_PLAN_CODE = 'PLN_prih2qg54fputl9';

// ─────────────────────────────────────────────────────────────
// Client-side mirror of the backend's isNewLocalDay()/normalizeTimezone()
// in functions/index.js. The backend only resets messagesUsedToday /
// voiceNotesUsedToday lazily, on the first send of a new day — so the
// Firestore doc can sit at yesterday's count all morning until someone
// actually sends a message. Without this check, the UI displays that
// stale count as if it were still accurate. This does NOT touch
// Firestore — it only decides what to show, the same way the backend
// decides whether to reset before checking the limit.
// ─────────────────────────────────────────────────────────────
function normalizeTimezone(tz) {
  if (!tz || typeof tz !== 'string') return 'UTC';
  if (DateTime.now().setZone(tz).isValid) return tz;
  const match = tz.match(/^GMT([+-]\d{1,2}(:\d{2})?)$/i);
  if (match) {
    const converted = `UTC${match[1]}`;
    if (DateTime.now().setZone(converted).isValid) return converted;
  }
  return 'UTC';
}

function isNewLocalDay(lastReset, timezone) {
  const zone = normalizeTimezone(timezone);
  if (!lastReset) return true;
  const lastDate = lastReset.toDate ? lastReset.toDate() : new Date(lastReset);
  const last = DateTime.fromJSDate(lastDate, { zone });
  const now = DateTime.now().setZone(zone);
  return last.toISODate() !== now.toISODate();
}

const styleTag = `
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(16px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.4; }
  }
  @keyframes messageIn {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .chat-input:focus {
    border-color: #008081 !important;
    box-shadow: 0 0 0 3px rgba(0,128,129,0.15) !important;
    outline: none !important;
  }
  .chat-send-btn {
    transition: transform 0.2s, box-shadow 0.2s !important;
  }
  .chat-send-btn:hover:not(:disabled) {
    transform: translateY(-1px) !important;
    box-shadow: 0 6px 20px rgba(0,128,129,0.4) !important;
  }
  .chat-icon-btn {
    transition: background-color 0.2s, border-color 0.2s !important;
  }
  .chat-icon-btn:hover {
    border-color: rgba(0,128,129,0.5) !important;
    background-color: rgba(0,128,129,0.08) !important;
  }
  .chat-header-btn {
    transition: background-color 0.2s, border-color 0.2s !important;
  }
  .chat-header-btn:hover {
    background-color: rgba(0,128,129,0.08) !important;
    border-color: rgba(0,128,129,0.3) !important;
  }
  .premium-plan-btn {
    transition: transform 0.2s, box-shadow 0.2s, opacity 0.2s !important;
  }
  .premium-plan-btn:hover:not(:disabled) {
    transform: translateY(-2px) !important;
    box-shadow: 0 8px 24px rgba(0,0,0,0.3) !important;
  }
  .strike-modal-btn {
    transition: transform 0.2s, box-shadow 0.2s !important;
  }
  .strike-modal-btn:hover {
    transform: translateY(-1px) !important;
  }
`;

function PremiumModal({ onClose, theme, t, reason, userEmail, userId }) {
  const { t: translate } = useLanguage();
  const [paystackLoaded, setPaystackLoaded] = useState(false);

  useEffect(() => {
    if (window.PaystackPop) { setPaystackLoaded(true); return; }
    const existing = document.getElementById('paystack-inline-script');
    if (existing) {
      existing.addEventListener('load', () => setPaystackLoaded(true));
      return;
    }
    const script = document.createElement('script');
    script.id = 'paystack-inline-script';
    script.src = 'https://js.paystack.co/v1/inline.js';
    script.async = true;
    script.onload = () => setPaystackLoaded(true);
    document.body.appendChild(script);
  }, []);

  const handleUpgrade = (planCode, planName) => {
    if (!paystackLoaded || !window.PaystackPop) {
      alert('Payment system is loading, please try again in a moment.');
      return;
    }
    const amount = planCode === MONTHLY_PLAN_CODE ? 64500 : 258000;
    const handler = window.PaystackPop.setup({
      key: PAYSTACK_PUBLIC_KEY,
      email: userEmail,
      amount: amount,
      currency: 'KES',
      plan: planCode,
      metadata: {
        custom_fields: [
          { display_name: 'User ID', variable_name: 'userId', value: userId },
          { display_name: 'Plan Name', variable_name: 'planName', value: planName }
        ]
      },
      callback: function(response) {
        console.log('Payment successful:', response.reference);
      },
      onClose: function() {}
    });
    handler.openIframe();
  };

  return (
    <div style={{
      position: 'fixed', inset: 0,
      backgroundColor: 'rgba(0,0,0,0.75)',
      backdropFilter: 'blur(6px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: '1rem',
      animation: 'fadeInUp 0.25s ease'
    }}>
      <div style={{
        background: 'linear-gradient(145deg, rgba(255,255,255,0.07), rgba(255,255,255,0.03))',
        backdropFilter: 'blur(20px)',
        borderRadius: '1.5rem',
        padding: '2rem',
        maxWidth: '380px',
        width: '100%',
        boxShadow: '0 25px 60px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.1)',
        border: '1px solid rgba(255,255,255,0.1)'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '1.75rem' }}>
          <div style={{
            width: '64px', height: '64px', margin: '0 auto 1rem',
            background: 'linear-gradient(135deg, #008081, #4B2C5E)',
            borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '1.75rem',
            boxShadow: '0 8px 24px rgba(0,128,129,0.35)'
          }}>✨</div>
          <h2 style={{ margin: 0, color: '#ffffff', fontSize: '1.35rem', fontWeight: '800', letterSpacing: '-0.02em' }}>
            {translate('upgradeToPremium')}
          </h2>
          <p style={{ margin: '0.5rem 0 0', color: 'rgba(255,255,255,0.55)', fontSize: '0.875rem', lineHeight: 1.6 }}>
            {reason === 'messages' && translate('dailyMessageLimitReached')}
            {reason === 'voice' && translate('dailyVoiceLimitReached')}
            {!reason && translate('unlockUnlimited')}
          </p>
        </div>

        <div style={{
          backgroundColor: 'rgba(255,255,255,0.04)',
          borderRadius: '1rem',
          padding: '0.25rem 0',
          marginBottom: '1.5rem',
          border: '1px solid rgba(255,255,255,0.06)'
        }}>
          {[
            { icon: '✉️', label: translate('unlimitedMessages') },
            { icon: '🎤', label: translate('unlimitedVoiceNotes') },
            { icon: '👥', label: translate('unlimitedGroupMessages') },
            { icon: '🌍', label: translate('unlimitedCommunityMessages') },
          ].map((feature, i, arr) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: '0.75rem',
              padding: '0.75rem 1rem',
              color: 'rgba(255,255,255,0.85)',
              fontSize: '0.875rem',
              borderBottom: i < arr.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none'
            }}>
              <span style={{ fontSize: '1.1rem' }}>{feature.icon}</span>
              {feature.label}
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '0.75rem' }}>
          <button
            className="premium-plan-btn"
            onClick={() => handleUpgrade(MONTHLY_PLAN_CODE, 'monthly')}
            disabled={!paystackLoaded}
            style={{
              padding: '1rem',
              background: 'linear-gradient(135deg, #008081, #005f5f)',
              color: '#FFFFFF', border: 'none', borderRadius: '0.875rem',
              fontWeight: '700', cursor: paystackLoaded ? 'pointer' : 'not-allowed', fontSize: '0.95rem',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              boxShadow: '0 4px 15px rgba(0,128,129,0.35)',
              opacity: paystackLoaded ? 1 : 0.7
            }}
          >
            <span>{translate('monthly')}</span>
            <span style={{ fontSize: '1.1rem' }}>{translate('pricePerMonth', { price: 5 })}</span>
          </button>

          <button
            className="premium-plan-btn"
            onClick={() => handleUpgrade(SIX_MONTH_PLAN_CODE, 'sixMonth')}
            disabled={!paystackLoaded}
            style={{
              padding: '1rem',
              background: 'linear-gradient(135deg, #4B2C5E, #7c3aed)',
              color: '#FFFFFF', border: 'none', borderRadius: '0.875rem',
              fontWeight: '700', cursor: paystackLoaded ? 'pointer' : 'not-allowed', fontSize: '0.95rem',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              boxShadow: '0 4px 15px rgba(75,44,94,0.35)',
              opacity: paystackLoaded ? 1 : 0.7
            }}
          >
            <span>{translate('sixMonths')} <span style={{ fontSize: '0.75rem', opacity: 0.75, marginLeft: '0.35rem' }}>{translate('savePercent', { percent: 33 })}</span></span>
            <span style={{ fontSize: '1.1rem' }}>{translate('priceTotal', { price: 20 })}</span>
          </button>
        </div>

        <button
          onClick={onClose}
          style={{
            width: '100%', padding: '0.75rem',
            backgroundColor: 'transparent',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '0.875rem',
            color: 'rgba(255,255,255,0.45)',
            cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500',
          }}
        >
          {translate('maybeLater')}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// STRIKE / BAN WARNING MODAL
// Shown when a message is blocked by the server-side content filter.
// Tone is deliberately warm and non-punitive at strikes 1-2, and
// direct (without being cold) at a permanent ban.
// ─────────────────────────────────────────────────────────────
function StrikeWarningModal({ strikes, banned, onClose }) {
  return (
    <div style={{
      position: 'fixed', inset: 0,
      backgroundColor: 'rgba(0,0,0,0.75)',
      backdropFilter: 'blur(6px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: '1rem',
      animation: 'fadeInUp 0.25s ease'
    }}>
      <div style={{
        background: 'linear-gradient(145deg, rgba(255,255,255,0.07), rgba(255,255,255,0.03))',
        backdropFilter: 'blur(20px)',
        borderRadius: '1.5rem',
        padding: '2rem',
        maxWidth: '380px',
        width: '100%',
        boxShadow: '0 25px 60px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.1)',
        border: banned ? '1px solid rgba(224,92,92,0.3)' : '1px solid rgba(250,204,21,0.25)',
        textAlign: 'center'
      }}>
        <div style={{
          width: '64px', height: '64px', margin: '0 auto 1.25rem',
          background: banned ? 'rgba(224,92,92,0.15)' : 'rgba(250,204,21,0.12)',
          border: banned ? '1px solid rgba(224,92,92,0.3)' : '1px solid rgba(250,204,21,0.25)',
          borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '1.75rem'
        }}>
          {banned ? '🔒' : '💛'}
        </div>

        <h2 style={{ margin: 0, color: '#ffffff', fontSize: '1.25rem', fontWeight: '800', letterSpacing: '-0.02em' }}>
          {banned ? 'Account disabled' : "Let's keep this space safe"}
        </h2>

        <p style={{ margin: '0.75rem 0 0', color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem', lineHeight: 1.7 }}>
          {banned
            ? "Your account has been permanently disabled after repeated messages that didn't meet Veyl's community guidelines."
            : "That message didn't go through — it didn't feel like it fit the space you and your partner are building together. Take a breath, and feel free to try rephrasing."}
        </p>

        {!banned && (
          <div style={{
            marginTop: '1.25rem',
            display: 'flex', justifyContent: 'center', gap: '0.5rem'
          }}>
            {[1, 2, 3].map(n => (
              <div key={n} style={{
                width: '32px', height: '6px', borderRadius: '3px',
                backgroundColor: n <= strikes ? '#facc15' : 'rgba(255,255,255,0.12)'
              }} />
            ))}
          </div>
        )}

        <button
          className="strike-modal-btn"
          onClick={onClose}
          style={{
            marginTop: '1.5rem', width: '100%', padding: '0.875rem',
            background: banned ? 'rgba(255,255,255,0.06)' : 'linear-gradient(135deg, #008081, #005f5f)',
            color: '#ffffff', border: banned ? '1px solid rgba(255,255,255,0.12)' : 'none',
            borderRadius: '0.875rem', fontWeight: '700', cursor: 'pointer', fontSize: '0.9rem',
            boxShadow: banned ? 'none' : '0 4px 15px rgba(0,128,129,0.35)'
          }}
        >
          {banned ? 'Close' : 'Got it'}
        </button>
      </div>
    </div>
  );
}

export default function ChatView({ user, userData, partner, setUserData, activeVeylId, theme, t }) {
  const { t: translate } = useLanguage();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(true);
  const [showVoiceNote, setShowVoiceNote] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showUnmatchConfirm, setShowUnmatchConfirm] = useState(false);
  const [unmatching, setUnmatching] = useState(false);
  const [hoveredMsgId, setHoveredMsgId] = useState(null);
  const [deletingMsgId, setDeletingMsgId] = useState(null);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [premiumReason, setPremiumReason] = useState(null);
  const [strikeWarning, setStrikeWarning] = useState(null); // { strikes, banned } | null
  const messagesEndRef = useRef(null);

  const dark = theme === 'dark';

  const colors = {
    cardBg:        dark ? 'rgba(255,255,255,0.02)' : '#FFFFFF',
    cardBorder:    dark ? 'rgba(255,255,255,0.07)' : '#e5e7eb',
    headerBg:      dark ? 'rgba(255,255,255,0.02)' : '#f9fafb',
    headerBorder:  dark ? 'rgba(255,255,255,0.07)' : '#e5e7eb',
    msgAreaBg:     dark ? 'rgba(0,0,0,0.15)' : '#f3f4f6',
    inputAreaBg:   dark ? 'rgba(255,255,255,0.02)' : '#f9fafb',
    inputBg:       dark ? 'rgba(255,255,255,0.05)' : '#ffffff',
    inputBorder:   dark ? 'rgba(255,255,255,0.1)' : '#d1d5db',
    inputColor:    dark ? '#ffffff' : '#1B2D44',
    text:          dark ? '#ffffff' : '#1B2D44',
    subtext:       dark ? 'rgba(255,255,255,0.45)' : '#6b7280',
    partnerMsgBg:  dark ? 'rgba(255,255,255,0.07)' : '#e5e7eb',
    partnerMsgBorder: dark ? 'rgba(255,255,255,0.08)' : '#d1d5db',
    partnerMsgText: dark ? '#ffffff' : '#1B2D44',
    emptyText:     dark ? 'rgba(255,255,255,0.3)' : '#9ca3af',
    btnBg:         dark ? 'rgba(255,255,255,0.05)' : '#f3f4f6',
    btnBorder:     dark ? 'rgba(255,255,255,0.1)' : '#d1d5db',
    btnText:       dark ? 'rgba(255,255,255,0.6)' : '#4b5563',
    deletingText:  dark ? 'rgba(255,255,255,0.35)' : '#9ca3af',
    iconBtnBg:     dark ? 'rgba(255,255,255,0.05)' : '#f3f4f6',
    iconBtnBorder: dark ? 'rgba(255,255,255,0.1)' : '#d1d5db',
    iconBtnColor:  dark ? 'rgba(255,255,255,0.5)' : '#6b7280',
    onlineDotBorder: dark ? '#080f1a' : '#ffffff',
    skeletonBg:    dark ? 'rgba(255,255,255,0.06)' : '#e5e7eb',
  };

  const partnerVeylId = userData?.activeVeyl?.partnerVeylId;
  const chatId = activeVeylId && partnerVeylId
    ? [activeVeylId, partnerVeylId].sort().join('_')
    : null;

  // Mirrors the backend's lazy-reset check (functions/index.js, isNewLocalDay)
  // so the counters shown here match what a real send would actually do,
  // instead of trusting a Firestore field that only updates on send.
  const veylTimezone = userData?.timezone || userData?.activeVeyl?.timezone || 'UTC';
  const lastMessageReset = userData?.activeVeyl?.lastMessageReset;
  const countersStale = isNewLocalDay(lastMessageReset, veylTimezone);

  const dailyLimit = 10;
  const messagesUsed = countersStale ? 0 : (userData?.activeVeyl?.messagesUsedToday || 0);
  const messagesLeft = userData?.isPremium ? 999 : (dailyLimit - messagesUsed);
  const voiceNotesUsed = countersStale ? 0 : (userData?.activeVeyl?.voiceNotesUsedToday || 0);
  const voiceNotesLeft = userData?.isPremium ? 999 : (10 - voiceNotesUsed);

  const myAvatarId = userData?.activeVeyl?.unveiled ? 'unveiled' : userData?.avatarId;
  const partnerAvatarId = partner?.unveiled ? 'unveiled' : partner?.avatarId;

  // Live messages for this chat.
  useEffect(() => {
    if (!chatId) return;
    setMessagesLoading(true);
    const q = query(collection(db, 'chats', chatId, 'messages'), orderBy('timestamp', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const msgs = [];
      snapshot.forEach(d => msgs.push({ id: d.id, ...d.data() }));
      setMessagesLoading(false);
      setMessages(msgs);
    });
    return () => unsubscribe();
  }, [chatId]);

  // "Your partner sent you a message" alerts are created by the server
  // (notifyPartnerMessage). While this chat is open the person is already
  // reading it, so mark any such alert for THIS chat as read straight away
  // instead of leaving a bell badge for messages they just saw.
  useEffect(() => {
    if (!chatId || !user?.uid) return;
    const q = query(
      collection(db, 'notifications'),
      where('recipientId', '==', user.uid),
      where('type', '==', 'partner_message'),
      where('read', '==', false)
    );
    const unsubscribe = onSnapshot(q, (snap) => {
      snap.forEach(d => {
        if (d.data().chatId === chatId) {
          updateDoc(doc(db, 'notifications', d.id), { read: true }).catch(() => {});
        }
      });
    }, () => {});
    return () => unsubscribe();
  }, [chatId, user?.uid]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || loading || !chatId) return;
    if (messagesLeft <= 0) {
      setPremiumReason('messages');
      setShowPremiumModal(true);
      return;
    }
    setLoading(true);
    try {
      const functions = getFunctions();
      const sendChatMessage = httpsCallable(functions, 'sendChatMessage');
      await sendChatMessage({
        veylId: activeVeylId,
        partnerVeylId,
        text: newMessage,
      });
      setNewMessage('');
    } catch (err) {
      if (err.code === 'functions/resource-exhausted') {
        setPremiumReason('messages');
        setShowPremiumModal(true);
      } else if (err.code === 'functions/failed-precondition' && err.details?.moderationBlocked) {
        setStrikeWarning({ strikes: err.details.strikes, banned: err.details.banned });
      } else {
        alert(translate('error') + ': ' + err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVoiceNoteLimitReached = () => {
    setPremiumReason('voice');
    setShowPremiumModal(true);
  };

  const deleteMessage = async (msgId) => {
    if (!chatId) return;
    setDeletingMsgId(msgId);
    try {
      await deleteDoc(doc(db, 'chats', chatId, 'messages', msgId));
    } catch (err) {
      alert(translate('error') + ': ' + err.message);
    } finally {
      setDeletingMsgId(null);
    }
  };

  // Unmatching now happens on the server (the `unmatch` Cloud Function):
  // it resets both veyls, blocks the two people from each other, deletes
  // the chat and voice notes, and notifies the partner. The browser can't
  // do those things safely (it can't edit the partner's veyl or user doc,
  // and a notification sent after the match is gone is rejected).
  const handleUnmatch = async () => {
    if (!activeVeylId || !partnerVeylId) return;
    setUnmatching(true);
    try {
      const functions = getFunctions();
      const unmatchFn = httpsCallable(functions, 'unmatch');
      await unmatchFn({ veylId: activeVeylId });

      setUserData(prev => ({
        ...prev,
        activeVeyl: {
          ...prev.activeVeyl,
          partnerId: null, partnerVeylId: null, status: 'seeking_partner',
          matchedAt: null, messagesUsedToday: 0, voiceNotesUsedToday: 0,
        }
      }));
      setShowUnmatchConfirm(false);
    } catch (err) {
      alert(translate('error') + ': ' + err.message);
    } finally {
      setUnmatching(false);
    }
  };

  const handleReported = () => {
    setShowReportModal(false);
    setUserData(prev => ({
      ...prev,
      partnerId: null, status: 'seeking_partner',
      activeVeyl: { ...prev.activeVeyl, partnerId: null, status: 'seeking_partner' }
    }));
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (!chatId) {
    return (
      <>
        <style>{styleTag}</style>
        <div style={{
          background: colors.cardBg,
          backdropFilter: 'blur(10px)',
          borderRadius: '1.25rem',
          border: `1px solid ${colors.cardBorder}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          height: '600px', animation: 'fadeInUp 0.4s ease'
        }}>
          <div style={{ textAlign: 'center', color: colors.emptyText }}>
            <div style={{ fontSize: '3rem', marginBottom: '0.75rem' }}>💬</div>
            <p style={{ margin: 0, fontSize: '0.95rem' }}>{translate('waitingForMatch')}</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{styleTag}</style>

      <div style={{
        background: colors.cardBg,
        backdropFilter: 'blur(10px)',
        borderRadius: '1.25rem',
        border: `1px solid ${colors.cardBorder}`,
        display: 'flex', flexDirection: 'column',
        height: '600px',
        animation: 'fadeInUp 0.4s ease',
        overflow: 'hidden'
      }}>

        {/* Header */}
        <div style={{
          padding: '1rem 1.25rem',
          borderBottom: `1px solid ${colors.headerBorder}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: colors.headerBg
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem' }}>
            <div style={{ position: 'relative' }}>
              <Avatar username={partner?.name || translate('yourPartner')} avatarId={partnerAvatarId} size={42} />
              <span style={{
                position: 'absolute', bottom: 1, right: 1,
                width: '10px', height: '10px', borderRadius: '50%',
                backgroundColor: '#16a34a',
                border: `2px solid ${colors.onlineDotBorder}`,
                animation: 'pulse 2s infinite'
              }} />
            </div>
            <div>
              <div style={{ fontWeight: '700', color: colors.text, fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                {partner?.name || translate('yourPartner')}
                {partner?.unveiled && (
                  <span style={{ fontSize: '0.65rem', color: '#c084fc', fontWeight: '700', backgroundColor: 'rgba(192,132,252,0.12)', padding: '0.15rem 0.45rem', borderRadius: '9999px' }}>
                    {translate('unveiled')}
                  </span>
                )}
              </div>
              <div style={{ fontSize: '0.78rem', color: colors.subtext, marginTop: '0.1rem' }}>
                {userData?.isPremium ? (
                  <span style={{ color: '#008081', fontWeight: '600' }}>✨ {translate('premiumUnlimited')}</span>
                ) : (
                  <span>
                    {messagesLeft} {translate('msg')} · {voiceNotesLeft} 🎤 ·{' '}
                    <span
                      onClick={() => { setPremiumReason(null); setShowPremiumModal(true); }}
                      style={{ color: '#008081', cursor: 'pointer', fontWeight: '600' }}
                    >
                      ✨ {translate('upgrade')}
                    </span>
                  </span>
                )}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="chat-header-btn" onClick={() => setShowUnmatchConfirm(!showUnmatchConfirm)}
              style={{ background: colors.btnBg, border: `1px solid ${colors.btnBorder}`, borderRadius: '0.5rem', padding: '0.4rem 0.75rem', cursor: 'pointer', color: colors.btnText, fontSize: '0.78rem', fontWeight: '500' }}>
              {translate('unmatch')}
            </button>
            <button className="chat-header-btn" onClick={() => setShowReportModal(true)}
              style={{ background: colors.btnBg, border: `1px solid ${colors.btnBorder}`, borderRadius: '0.5rem', padding: '0.4rem 0.75rem', cursor: 'pointer', color: colors.btnText, fontSize: '0.78rem', fontWeight: '500' }}>
              {translate('report')}
            </button>
          </div>
        </div>

        {/* Unmatch confirmation banner */}
        {showUnmatchConfirm && (
          <div style={{ padding: '1rem 1.25rem', backgroundColor: 'rgba(224,92,92,0.08)', borderBottom: '1px solid rgba(224,92,92,0.2)', animation: 'fadeInUp 0.2s ease' }}>
            <p style={{ fontSize: '0.85rem', color: '#E05C5C', margin: '0 0 0.75rem', fontWeight: '500', lineHeight: 1.5 }}>
              {translate('unmatchWarning')}
            </p>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button onClick={() => setShowUnmatchConfirm(false)}
                style={{ flex: 1, padding: '0.5rem', border: `1px solid ${colors.btnBorder}`, backgroundColor: 'transparent', color: colors.btnText, borderRadius: '0.5rem', fontWeight: '600', cursor: 'pointer', fontSize: '0.8rem' }}>
                {translate('cancel')}
              </button>
              <button onClick={handleUnmatch} disabled={unmatching}
                style={{ flex: 1, padding: '0.5rem', backgroundColor: '#E05C5C', color: '#FFFFFF', border: 'none', borderRadius: '0.5rem', fontWeight: '600', cursor: unmatching ? 'not-allowed' : 'pointer', opacity: unmatching ? 0.6 : 1, fontSize: '0.8rem' }}>
                {unmatching ? translate('unmatching') : translate('yesUnmatch')}
              </button>
            </div>
          </div>
        )}

        {/* Messages area */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '1.25rem', background: colors.msgAreaBg }}>
          {messagesLoading ? (
            /* Skeleton loading state — mimics real message bubble layout */
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {[
                { side: 'left',  width: '55%' },
                { side: 'right', width: '40%' },
                { side: 'left',  width: '65%' },
                { side: 'right', width: '50%' },
                { side: 'left',  width: '45%' },
              ].map((item, i) => (
                <div key={i} style={{
                  display: 'flex',
                  justifyContent: item.side === 'right' ? 'flex-end' : 'flex-start',
                  alignItems: 'flex-end',
                  gap: '0.5rem',
                  animationDelay: `${i * 0.08}s`
                }}>
                  {item.side === 'left' && (
                    <div style={{
                      width: 30, height: 30, borderRadius: '50%',
                      backgroundColor: colors.skeletonBg,
                      flexShrink: 0,
                      animation: 'pulse 1.6s ease-in-out infinite',
                      animationDelay: `${i * 0.1}s`
                    }} />
                  )}
                  <div style={{
                    height: '44px',
                    width: item.width,
                    borderRadius: item.side === 'right'
                      ? '1rem 1rem 0.25rem 1rem'
                      : '1rem 1rem 1rem 0.25rem',
                    backgroundColor: colors.skeletonBg,
                    animation: 'pulse 1.6s ease-in-out infinite',
                    animationDelay: `${i * 0.1}s`
                  }} />
                  {item.side === 'right' && (
                    <div style={{
                      width: 30, height: 30, borderRadius: '50%',
                      backgroundColor: colors.skeletonBg,
                      flexShrink: 0,
                      animation: 'pulse 1.6s ease-in-out infinite',
                      animationDelay: `${i * 0.1}s`
                    }} />
                  )}
                </div>
              ))}
            </div>
          ) : messages.length === 0 ? (
            <div style={{ textAlign: 'center', color: colors.emptyText, paddingTop: '3rem' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>💬</div>
              <p style={{ margin: 0, fontSize: '0.9rem' }}>{translate('noMessagesYet')}</p>
            </div>
          ) : (
            messages.map(msg => {
              const isMe = msg.senderId === user.uid;
              const isHovered = hoveredMsgId === msg.id;
              const isDeleting = deletingMsgId === msg.id;
              return (
                <div key={msg.id}
                  onMouseEnter={() => isMe && setHoveredMsgId(msg.id)}
                  onMouseLeave={() => setHoveredMsgId(null)}
                  style={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start', marginBottom: '1rem', gap: '0.5rem', alignItems: 'flex-end', animation: 'messageIn 0.2s ease' }}>
                  {!isMe && <Avatar username={msg.senderName} avatarId={partnerAvatarId} size={30} />}
                  <div style={{ maxWidth: '70%', display: 'flex', flexDirection: 'column', alignItems: isMe ? 'flex-end' : 'flex-start' }}>
                    <div style={{
                      padding: '0.75rem 1rem',
                      borderRadius: isMe ? '1rem 1rem 0.25rem 1rem' : '1rem 1rem 1rem 0.25rem',
                      background: isMe ? 'linear-gradient(135deg, #008081, #005f5f)' : colors.partnerMsgBg,
                      color: isMe ? '#ffffff' : colors.partnerMsgText,
                      border: isMe ? 'none' : `1px solid ${colors.partnerMsgBorder}`,
                      opacity: isDeleting ? 0.4 : 1, transition: 'opacity 0.2s',
                      boxShadow: isMe ? '0 4px 12px rgba(0,128,129,0.25)' : 'none'
                    }}>
                      {(!msg.type || msg.type === 'text') && (
                        <span style={{ fontSize: '0.95rem', lineHeight: 1.5 }}>{msg.text}</span>
                      )}
                      {msg.type === 'voice' && (
                        <VoiceNotePlayer audioUrl={msg.audioUrl} duration={msg.duration} modulation={msg.modulation} theme={theme} />
                      )}
                      <div style={{ fontSize: '0.68rem', opacity: 0.5, marginTop: '0.3rem', textAlign: isMe ? 'right' : 'left' }}>
                        {formatTime(msg.timestamp)}
                      </div>
                    </div>
                    {isMe && isHovered && !isDeleting && (
                      <button onClick={() => deleteMessage(msg.id)}
                        style={{ marginTop: '0.25rem', padding: '0.15rem 0.5rem', backgroundColor: 'transparent', border: 'none', borderRadius: '999px', cursor: 'pointer', color: '#E05C5C', fontSize: '0.7rem', fontWeight: '500', opacity: 0.8 }}>
                        {translate('deleteMessage')}
                      </button>
                    )}
                    {isDeleting && (
                      <span style={{ fontSize: '0.7rem', color: colors.deletingText, marginTop: '0.2rem' }}>
                        {translate('deleting')}
                      </span>
                    )}
                  </div>
                  {isMe && <Avatar username={userData.name} avatarId={myAvatarId} size={30} />}
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Voice Note Panel */}
        {showVoiceNote && (
          <div style={{ padding: '0 1rem', background: colors.inputAreaBg, borderTop: `1px solid ${colors.cardBorder}` }}>
            <VoiceNote
              user={user}
              userData={userData}
              chatId={chatId}
              setUserData={setUserData}
              veylId={activeVeylId}
              partnerVeylId={partnerVeylId}
              theme={theme}
              onSent={() => setShowVoiceNote(false)}
              onLimitReached={handleVoiceNoteLimitReached}
            />
          </div>
        )}

        {/* Low message warning */}
        {messagesLeft <= 3 && messagesLeft > 0 && !userData?.isPremium && (
          <div style={{ padding: '0.5rem 1.25rem', background: 'rgba(192,132,252,0.06)', borderTop: '1px solid rgba(192,132,252,0.15)' }}>
            <p style={{ fontSize: '0.75rem', color: '#c084fc', margin: 0, textAlign: 'center' }}>
              {translate('messagesLeftWarning', { count: messagesLeft })}{' '}
              <span onClick={() => { setPremiumReason('messages'); setShowPremiumModal(true); }}
                style={{ textDecoration: 'underline', cursor: 'pointer', fontWeight: '600' }}>
                {translate('upgrade')}
              </span>{' '}
              {translate('forUnlimited')}
            </p>
          </div>
        )}

        {/* Limit reached upgrade prompt */}
        {messagesLeft <= 0 && !userData?.isPremium && (
          <div style={{ padding: '0.75rem 1.25rem', borderTop: `1px solid ${colors.cardBorder}`, textAlign: 'center' }}>
            <button onClick={() => { setPremiumReason('messages'); setShowPremiumModal(true); }}
              style={{ padding: '0.6rem 1.5rem', background: 'linear-gradient(135deg, #4B2C5E, #7c3aed)', color: '#FFFFFF', border: 'none', borderRadius: '0.75rem', fontWeight: '700', cursor: 'pointer', fontSize: '0.85rem', boxShadow: '0 4px 15px rgba(75,44,94,0.35)' }}>
              ✨ {translate('upgrade')}
            </button>
          </div>
        )}

        {/* Input area */}
        <form onSubmit={sendMessage} style={{ padding: '1rem 1.25rem', borderTop: `1px solid ${colors.headerBorder}`, background: colors.inputAreaBg }}>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <button type="button" className="chat-icon-btn"
              onClick={() => {
                if (voiceNotesLeft <= 0) { setPremiumReason('voice'); setShowPremiumModal(true); return; }
                setShowVoiceNote(!showVoiceNote);
              }}
              style={{
                padding: '0.75rem',
                backgroundColor: showVoiceNote ? 'rgba(0,128,129,0.15)' : colors.iconBtnBg,
                border: `1px solid ${showVoiceNote ? 'rgba(0,128,129,0.5)' : colors.iconBtnBorder}`,
                borderRadius: '0.75rem', cursor: 'pointer', fontSize: '1.2rem',
                color: showVoiceNote ? '#008081' : colors.iconBtnColor,
                flexShrink: 0,
                opacity: (!userData?.isPremium && voiceNotesLeft <= 0) ? 0.4 : 1
              }}>
              🎤
            </button>
            <input type="text" className="chat-input" value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder={messagesLeft <= 0 ? translate('dailyLimitReached') : translate('typeMessage')}
              disabled={messagesLeft <= 0}
              style={{
                flex: 1, padding: '0.75rem 1rem',
                border: `1px solid ${colors.inputBorder}`,
                borderRadius: '0.75rem', fontSize: '0.95rem',
                backgroundColor: colors.inputBg, color: colors.inputColor,
                opacity: messagesLeft <= 0 ? 0.4 : 1,
                boxSizing: 'border-box', transition: 'border-color 0.2s, box-shadow 0.2s'
              }} />
            <button type="submit" className="chat-send-btn"
              disabled={loading || !newMessage.trim() || messagesLeft <= 0}
              style={{
                padding: '0.75rem 1.25rem',
                background: 'linear-gradient(135deg, #008081, #005f5f)',
                color: '#FFFFFF', borderRadius: '0.75rem', fontWeight: '700', border: 'none',
                cursor: (loading || !newMessage.trim() || messagesLeft <= 0) ? 'not-allowed' : 'pointer',
                opacity: (loading || !newMessage.trim() || messagesLeft <= 0) ? 0.4 : 1,
                flexShrink: 0, boxShadow: '0 4px 15px rgba(0,128,129,0.3)', fontSize: '0.9rem'
              }}>
              {translate('send')}
            </button>
          </div>
        </form>
      </div>

      {showPremiumModal && (
        <PremiumModal
          onClose={() => setShowPremiumModal(false)}
          theme={theme}
          t={t}
          reason={premiumReason}
          userEmail={user.email}
          userId={user.uid}
        />
      )}

      {showReportModal && (
        <ReportModal
          user={user}
          userData={userData}
          partner={partner}
          onClose={() => setShowReportModal(false)}
          onReported={handleReported}
        />
      )}

      {strikeWarning && (
        <StrikeWarningModal
          strikes={strikeWarning.strikes}
          banned={strikeWarning.banned}
          onClose={() => setStrikeWarning(null)}
        />
      )}
    </>
  );
}

function VoiceNotePlayer({ audioUrl, duration, modulation, theme }) {
  const { t: translate } = useLanguage();
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef(null);
  const dark = theme === 'dark';

  useEffect(() => {
    return () => { if (audioRef.current) audioRef.current.pause(); };
  }, []);

  const togglePlay = () => {
    if (!audioRef.current || !audioUrl) return;
    if (isPlaying) { audioRef.current.pause(); setIsPlaying(false); }
    else { audioRef.current.play(); setIsPlaying(true); }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!audioUrl) return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      <span>❌</span><span style={{ fontSize: '0.875rem' }}>{translate('failedToLoad')}</span>
    </div>
  );

  const progress = duration ? (currentTime / duration) * 100 : 0;
  const progressBg   = dark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.15)';
  const progressFill = dark ? '#ffffff' : '#008081';
  const btnBg        = dark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: '180px', padding: '0.25rem' }}>
      <button onClick={togglePlay}
        style={{ width: '34px', height: '34px', borderRadius: '50%', border: 'none', backgroundColor: btnBg, color: 'inherit', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.9rem', flexShrink: 0 }}>
        {isPlaying ? '⏸' : '▶'}
      </button>
      <div style={{ flex: 1 }}>
        <div style={{ height: '3px', backgroundColor: progressBg, borderRadius: '2px', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${progress}%`, backgroundColor: progressFill, transition: 'width 0.1s linear' }} />
        </div>
        <div style={{ fontSize: '0.68rem', marginTop: '0.25rem', display: 'flex', justifyContent: 'space-between', opacity: 0.6 }}>
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration || 0)}</span>
        </div>
      </div>
      <span style={{ fontSize: '0.8rem', opacity: 0.7 }}>
        {modulation === 'deeper' && '🔉'}
        {modulation === 'neutral' && '🎭'}
        {modulation === 'higher' && '🔊'}
      </span>
      <audio ref={audioRef} src={audioUrl}
        onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime || 0)}
        onEnded={() => { setIsPlaying(false); setCurrentTime(0); }}
        style={{ display: 'none' }} />
    </div>
  );
}